import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "That reset link is invalid or has expired."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans flex items-center justify-center px-5 py-16">
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(circle at 70% 25%, #E2E30A15 0%, transparent 55%)" }} />
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative w-full max-w-[420px]"
        data-testid="reset-password-page"
      >
        <Link to="/login" className="flex items-center gap-3 mb-10">
          <Logo className="h-12 w-12" />
          <p className="font-display text-lg uppercase tracking-tight">
            <span className="text-[#E2E30A]">Performance</span> Nutrition
          </p>
        </Link>

        <h1 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter font-black leading-[0.95]">
          Set a new <span className="text-[#E2E30A]">password.</span>
        </h1>
        {!token ? (
          <p className="text-sm text-zinc-400 mt-3" data-testid="reset-missing-token">
            This link is missing its security token. Please request a new reset link.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">New password</label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="reset-password-input"
                  type={showPw ? "text" : "password"}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="h-12 rounded-none pr-11 bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
                />
                <button
                  type="button"
                  data-testid="reset-toggle-password-visibility"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-zinc-500 hover:text-white transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Confirm password</label>
              <Input
                data-testid="reset-confirm-input"
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat your new password"
                className="mt-1.5 h-12 rounded-none bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
              />
            </div>
            <Button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full h-13 sm:h-14 rounded-none bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-widest text-sm sm:text-base transition-colors group"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <span className="inline-flex items-center gap-2">
                  Update password
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>
          </form>
        )}
        <Link to="/login" data-testid="reset-back-to-signin"
          className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold hover:text-white transition-colors">
          ← Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
